import React, { useState, useEffect, useCallback } from 'react';
import {
    ShieldCheck, Bell, Mail, CheckCircle2, XCircle,
    Clock, User, Hash, Calendar, X,
    Activity, Layers, RefreshCw, Eye
} from 'lucide-react';
import Pagination from 'modules/swiftboard/components/Pagination';
import TableGrid from 'modules/swiftboard/components/TableGrid';
import NoDataState from 'modules/swiftboard/components/NoDataState';
import TableToolBar from 'modules/swiftboard/components/TableToolBar';
import { GlobalAdvanceFilter } from 'modules/swiftboard/components/advance-filter';
import GlobalDetailDrawer from 'modules/swiftboard/components/GlobalDetailDrawer';
import apiAuditTrail from '../api';
import Loader from 'components/ui/loader';
import { emailAuditTrailFilterFields } from './features/email-audit-trail.filter';

const EMPTY_FILTERS = {};



// ─── Config ───────────────────────────────────────────────────────────────────
const TYPE_CONFIG = {
    AUTH: { label: 'Auth', icon: ShieldCheck, bg: 'bg-primary_tint', text: 'text-primary', border: 'border-primary' },
    PROJECT_NOTIFICATION: { label: 'Project', icon: Bell, bg: 'bg-primary_tint', text: 'text-primary', border: 'border-primary' },
    OTHER: { label: 'Other', icon: Mail, bg: 'bg-surface_alt', text: 'text-text_secondary', border: 'border-border' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (iso) => !iso ? '—' : new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
const fmtShort = (iso) => !iso ? '—' : new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const initials = (e) => e.split('@')[0].split(/[._]/).map(p => p[0]?.toUpperCase()).join('').slice(0, 2);
const AVATAR_COLORS = ['bg-primary_tint text-primary', 'bg-primary_tint text-primary', 'bg-secondary_tint text-secondary', 'bg-secondary_tint text-secondary', 'bg-warning_bg text-warning'];
const avatarColor = (e) => AVATAR_COLORS[e.charCodeAt(0) % AVATAR_COLORS.length];

// ─── Cell Renderers ───────────────────────────────────────────────────────────
const TypeBadge = ({ type }) => {
    const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.OTHER;
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
            <Icon size={11} />{cfg.label}
        </span>
    );
};

const StatusPill = ({ success }) =>
    success
        ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-success_bg text-success border border-success_border"><CheckCircle2 size={11} />Success</span>
        : <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-danger_bg text-danger border border-danger_border"><XCircle size={11} />Failed</span>;

const AttemptBadge = ({ count }) => {
    const color = count === 1 ? 'bg-surface_alt text-text_muted' : count === 2 ? 'bg-warning_bg text-warning' : 'bg-danger_bg text-danger';
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold ${color}`}><RefreshCw size={9} />{count}x</span>;
};

// ─── TableGrid Column Definitions ────────────────────────────────────────────
const buildColumns = (onSubjectClick) => [
    {
        key: 'id',
        label: 'ID',
        width: '60px',
        sortable: true,
        render: (row) => <span className="text-xs font-mono text-text_muted">#{row.id}</span>,
    },
    {
        key: 'email_type',
        label: 'Type',
        width: '110px',
        sortable: true,
        render: (row) => <TypeBadge type={row.email_type} />,
    },
    {
        key: 'recipient_email',
        label: 'Recipient',
        width: '220px',
        sortable: true,
        render: (row) => (
            <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarColor(row.recipient_email)}`}>
                    {initials(row.recipient_email)}
                </div>
                <div className="min-w-0">
                    <p className="text-xs font-medium text-text_primary truncate max-w-[150px]">{row.recipient_email}</p>
                    <p className="text-xs text-text_muted font-mono">
                        {row.recipient_user_fid ? `uid:${row.recipient_user_fid}` : 'Guest'}
                    </p>
                </div>
            </div>
        ),
    },
    {
        key: 'subject',
        label: 'Subject / Preview',
        width: '280px',
        sortable: true,
        render: (row) => (
            <div
                className="min-w-0 max-w-[260px] cursor-pointer group"
                onClick={(e) => { e.stopPropagation(); onSubjectClick(row); }}
            >
                <p className="text-xs font-medium text-text_primary truncate group-hover:text-primary transition-colors duration-150">{row.subject}</p>
                {row.body && <p className="text-xs text-text_muted truncate mt-0.5">{row.body.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, 55)}…</p>}
            </div>
        ),
    },
    {
        key: 'attempt',
        label: 'Attempts',
        width: '90px',
        sortable: true,
        render: (row) => <AttemptBadge count={row.attempt || 1} />,
    },
    {
        key: 'is_success',
        label: 'Status',
        width: '120px',
        sortable: true,
        render: (row) => (
            <div>
                <StatusPill success={row.is_success} />
                {row.error && <p className="text-xs text-danger font-mono mt-1 truncate max-w-[100px]">{row.error.code}</p>}
            </div>
        ),
    },
    {
        key: 'created_date',
        label: 'Created At',
        width: '150px',
        sortable: true,
        render: (row) => (
            <div>
                <p className="text-xs text-text_primary whitespace-nowrap">{fmtShort(row.created_date)}</p>
                <p className="text-xs text-text_muted font-mono whitespace-nowrap">upd {fmtShort(row.updated_date)}</p>
            </div>
        ),
    },
];


// Inside EmailAuditTrail component, add this helper to build drawer config
const buildDrawerConfig = (record) => {
    console.log('viewing of id: ', record.id)
    if (!record) return null;

    const cfg = TYPE_CONFIG[record.email_type] || TYPE_CONFIG.OTHER;
    const Icon = cfg.icon;

    // Status banner as a custom React node (premium touch)
    const statusBanner = record.is_success ? (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-success_bg border border-success_border">
            <CheckCircle2 size={22} className="text-success" />
            <div>
                <p className="font-semibold text-success text-sm">Email delivered successfully</p>
                <p className="text-xs text-success">{record.attempt} attempt(s) · {fmtDate(record.created_date)}</p>
            </div>
        </div>
    ) : (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-danger_bg border border-danger_border">
            <XCircle size={22} className="text-danger" />
            <div>
                <p className="font-semibold text-danger text-sm">Delivery failed</p>
                <p className="text-xs text-danger">{record.attempt} attempt(s) · {fmtDate(record.created_date)}</p>
            </div>
        </div>
    );

    // Build field groups
    const mainFields = [
        { label: 'Record ID', value: `#${record.id}`, icon: <Hash size={13} />, copyable: true },
        { label: 'Email Type', value: <TypeBadge type={record.email_type} />, icon: <Layers size={13} /> },
        { label: 'Recipient', value: record.recipient_email, icon: <Mail size={13} />, copyable: true },
        { label: 'User FID', value: record.recipient_user_fid ? `#${record.recipient_user_fid}` : 'Guest', icon: <User size={13} /> },
        { label: 'Attempts', value: `${record.attempt || 1}x`, icon: <RefreshCw size={13} /> },
        { label: 'Is Activated', value: record.is_activated ? '✓ Active' : '✗ Inactive', icon: <Activity size={13} /> },
    ];

    const dateFields = [
        { label: 'Created', value: fmtDate(record.created_date), icon: <Calendar size={13} />, copyable: true },
        { label: 'Updated', value: fmtDate(record.updated_date), icon: <Clock size={13} />, copyable: true },
    ];

    const contentFields = [
        { label: 'Subject', value: record.subject, copyable: true },
        {
            label: null,
            value: record.body ? (
                <div className="rounded-xl border border-border bg-surface p-4 max-h-[400px] overflow-auto">
                    <div
                        className="prose prose-sm max-w-none break-words"
                        dangerouslySetInnerHTML={{ __html: record.body }}
                    />
                </div>
            ) : '—',
        }
    ];

    const sections = [
        { title: 'Status', fields: [{ label: null, value: statusBanner }] },
        { title: 'Email Metadata', fields: mainFields },
        { title: 'Timestamps', fields: dateFields },
        { title: 'Content', fields: contentFields },
    ];

    // Add error section if present
    if (record.error) {
        const errorFields = [
            { label: 'Error Code', value: record.error.code, copyable: true },
            { label: 'SMTP Code', value: record.error.smtp_code || '—' },
            { label: 'Message', value: record.error.message, copyable: true },
            {
                label: 'Raw JSON',
                value: (
                    <details>
                        <summary className="cursor-pointer text-primary">Show raw</summary>
                        <pre className="mt-2 text-xs bg-surface_alt p-2 rounded overflow-x-auto">
                            {JSON.stringify(record.error, null, 2)}
                        </pre>
                    </details>
                ),
            },
        ];
        sections.push({ title: 'Error Details', fields: errorFields });
    }

    return {
        title: 'Email Detail View',
        subtitle: `Audit Record #${record.id}`,
        icon: <Icon size={20} className={cfg.text} />,
        sections,
    };
};


// ─── Main Page ────────────────────────────────────────────────────────────────
const EmailAuditTrail = ({onUnreadChange}) => {
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [sortConfig, setSortConfig] = useState({ key: 'created_date', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selected, setSelected] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [drawerLoading, setDrawerLoading] = useState(false);

    const [loading, setLoading] = useState(true);
    const [resetSearch, setResetSearch] = useState(0);
    const [emailPreview, setEmailPreview] = useState(null);

    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);




    const fetchAuditTrail = useCallback(async () => {
        try {
            setLoading(true);

            const payload = {
                searchText: search || "",
                search: JSON.stringify(filters),
                limit: pageSize,
                skip: (currentPage - 1) * pageSize,
            };


            const response = await apiAuditTrail.getAll(payload);

            if (response.data.status) {
                setData(response.data.data.data);
                setTotalCount(response.data.total_count || 0);
                onUnreadChange?.(response.data.data.unread); 
            }

        } catch (error) {
            console.error("Failed to fetch audit trail:", error);
        } finally {
            setLoading(false);
        }
    }, [
        search,
        filters,
        currentPage,
        pageSize,
    ]);

    const fetchAuditById = async (id) => {
        try {
            setDrawerLoading(true);
            const response = await apiAuditTrail.getById(id);
            if (response.data.status) {
                setSelected(response.data.data); // full response
            }

        } catch (error) {
            console.error("Failed to fetch audit detail:", error);
        } finally {
            setDrawerLoading(false);
        }
    };

    useEffect(() => {
        fetchAuditTrail();
    }, [fetchAuditTrail]);

    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc' }));
        setCurrentPage(1);
    }, []);

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        if (size !== undefined && size !== pageSize) { setPageSize(size); setCurrentPage(1); }
    };

    const handleSearch = (val) => { setSearch(val); setCurrentPage(1); };

    const handleApplyFilters = ({ search: nextFilters }) => { setFilters(nextFilters); setCurrentPage(1); };

    const handleRefresh = () => {
        setLoading(true);
        setResetSearch(n => n + 1);
        setSearch('');
        setFilters(EMPTY_FILTERS);
        setCurrentPage(1);
        setTimeout(() => setLoading(false), 600);
        fetchAuditTrail(); 
    };
const renderActions = (row) => (
    <div className="flex items-center justify-center">
        <button
            onClick={(e) => {
                e.stopPropagation();
                setSelectedId(row.id);
                fetchAuditById(row.id);
            }}
            className="
                relative inline-flex items-center gap-2
                px-4 py-2
                text-xs font-semibold
                rounded-lg
                bg-primary
                text-white
                shadow-sm
                transition-all duration-200
                hover:bg-primary_hover
                hover:shadow-md
                active:scale-95
            "
        >
            {!row.is_read && (
                <span className="
                    absolute -top-1 -right-1
                    w-2.5 h-2.5
                    bg-danger
                    rounded-full
                    ring-2 ring-surface
                " />
            )}

            <Eye size={14} />
            View
        </button>
    </div>
);
    return (
        <div className="h-full overflow-y-auto bg-surface_alt">
            <div className="flex flex-col gap-4 pb-4">


                {/* ── Body ── */}

                <TableToolBar
                    onSearch={handleSearch}
                    onRefresh={handleRefresh}
                    resetSearchTrigger={resetSearch}
                    canCreate={false}
                    advanceFilter={
                        <GlobalAdvanceFilter
                            fields={emailAuditTrailFilterFields}
                            onApply={handleApplyFilters}
                            resetKey={resetSearch}
                        />
                    }
                />

                {/* TableGrid or NoDataState */}
                {!loading && totalCount === 0 ? (
                    <div className="flex items-center justify-center border rounded-lg border-border bg-surface min-h-[320px]">
                    <NoDataState
                        icon="search"
                        title="No Audit Records Found"
                        message="No records match your current search or filter criteria. Try adjusting the filters above."
                        showRefresh={false}
                        showBack={false}
                        size="medium"
                    />
                    </div>
                ) : (
                    <div className="bg-surface rounded-2xl border border-border shadow-sm overflow-hidden">
                        <TableGrid
                            data={data}
                            loading={loading}
                            columns={buildColumns(setEmailPreview)}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            showActions
                            selectable={false}
                            renderActions={renderActions}
                            onRowClick={(row) => {
                                setSelectedId(row.id);
                                fetchAuditById(row.id);
                            }}
                        />
                    </div>
                )}

                {/* Pagination */}
                {totalCount > 0 && (
                    <Pagination
                        totalCount={totalCount}
                        pageSize={pageSize}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>

            {/* Email Preview Modal */}
            {emailPreview && (
                <div
                    className="fixed inset-0 z-[1100] flex items-center justify-center p-4"
                    style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
                    onClick={() => setEmailPreview(null)}
                >
                    <div
                        className="relative w-full max-w-2xl max-h-[85vh] flex flex-col bg-surface rounded-2xl border border-border shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal header */}
                        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border flex-shrink-0">
                            <div className="min-w-0">
                                <p className="text-[11px] font-semibold text-text_muted uppercase tracking-widest mb-1">Subject</p>
                                <h3 className="text-[14px] font-semibold text-text_primary leading-snug">{emailPreview.subject || '—'}</h3>
                                <div className="flex items-center gap-3 mt-1.5">
                                    <span className="text-[11.5px] text-text_secondary">To: <span className="font-medium text-text_primary">{emailPreview.recipient_email}</span></span>
                                    <span className="text-border">·</span>
                                    <span className="text-[11.5px] text-text_muted">{fmtShort(emailPreview.created_date)}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => setEmailPreview(null)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-border bg-background text-text_secondary hover:bg-surface_alt flex-shrink-0 transition-colors duration-150"
                            >
                                <X size={15} />
                            </button>
                        </div>

                        {/* Mail body */}
                        <div className="flex-1 overflow-y-auto px-5 py-4">
                            {emailPreview.body ? (
                                <div
                                    className="prose prose-sm max-w-none text-text_primary break-words"
                                    dangerouslySetInnerHTML={{ __html: emailPreview.body }}
                                />
                            ) : (
                                <p className="text-sm text-text_muted text-center py-10">No email body available.</p>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-background flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <TypeBadge type={emailPreview.email_type} />
                                <StatusPill success={emailPreview.is_success} />
                            </div>
                            <button
                                onClick={() => setEmailPreview(null)}
                                className="px-4 py-1.5 rounded-lg border border-border bg-surface text-xs font-medium text-text_secondary hover:bg-surface_alt transition-colors duration-150"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail Drawer */}
            {(selectedId || drawerLoading) && (
                <GlobalDetailDrawer
                    open={!!selectedId}
                    onClose={() => {
                        setSelected(null);
                        setSelectedId(null);
                        fetchAuditTrail(); 
                    }}
                    {...(selected ? buildDrawerConfig(selected) : {
                        title: "Email Detail View",
                        subtitle: "Fetching full audit record...",
                        sections: [
                            {
                                title: "",
                                fields: [
                                    {
                                        label: null,
                                        value: (
                                            <div className="flex flex-col items-center justify-center py-24">
                                                <Loader type="premium-fast-dotted" size={40} color="var(--color-text-primary)" />
                                                <p className="mt-4 text-sm font-semibold text-text_primary">
                                                    Getting everything ready for you
                                                </p>
                                            </div>
                                        )
                                    }
                                ]
                            }
                        ]
                    })}
                />
            )}
        </div>
    );
};

export default EmailAuditTrail;